import { PageContent, PageLayout } from '@/components/ui/design-system';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/hooks/use-translation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAlloRemote } from '@/services/api-allo/client-remote';
import { SessionSummaryView } from '@/views/power-dialer/components/session-summary/session-summary-view';
import { useActivePowerDialer } from '@/views/power-dialer/providers/active-power-dialer-provider';
import {
  type DialingState,
  type EndedSessionContext,
  type SessionContact,
  getSessionContactKey,
} from '@/views/power-dialer/types';
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

type SessionSummarySearch = { from?: 'analytics' };

export const Route = createFileRoute('/_app/power-dialer/session/$sessionId')({
  component: SessionSummaryRoute,
  validateSearch: (search: Record<string, unknown>): SessionSummarySearch => ({
    from: search.from === 'analytics' ? 'analytics' : undefined,
  }),
  onEnter: () => analytics.track(ANALYTICS_EVENTS.POWER_DIALER.TAB_OPENED),
});

// Wire-format note: spec types are camelCase but responses come back snake_case
// (see CLAUDE.md). Read through this narrower shape rather than the generated type.
type WireAssignedNumber = {
  number?: string;
  contact?: {
    id?: string;
    full_name?: string;
    image_key?: string;
    job_title?: string;
    company?: { id?: string; name?: string };
  };
};

type WireSessionResponse = {
  id?: string;
  name?: string;
  owner?: { id?: string; name?: string };
  line?: { number?: string; name?: string };
  session_start?: string;
  session_end?: string;
  assigned_numbers?: WireAssignedNumber[];
};

// API doesn't expose per-contact outcomes today, so all surfaced rows get a
// neutral placeholder state. `getQueueRowOutcome` returns `undefined` for
// 'pending', so the trailing outcome label is left blank — better than a
// fabricated "Call answered".
const PLACEHOLDER_STATE: DialingState = 'pending';

const projectAssignedNumbers = (
  assignedNumbers: WireAssignedNumber[] | undefined,
): { contacts: SessionContact[]; dialingState: Record<string, DialingState> } => {
  if (!assignedNumbers || assignedNumbers.length === 0) {
    return { contacts: [], dialingState: {} };
  }
  const contacts: SessionContact[] = [];
  const dialingState: Record<string, DialingState> = {};
  for (const assigned of assignedNumbers) {
    const number = assigned.number;
    const apiContact = assigned.contact;
    const contact: SessionContact = {
      id: apiContact?.id,
      name: apiContact?.full_name,
      numbers: number ? [number] : undefined,
      job_title: apiContact?.job_title,
      company: apiContact?.company
        ? { id: apiContact.company.id, name: apiContact.company.name }
        : undefined,
      dialing_state: PLACEHOLDER_STATE,
    };
    contacts.push(contact);
    dialingState[getSessionContactKey(contact)] = PLACEHOLDER_STATE;
  }
  return { contacts, dialingState };
};

function SessionSummaryRoute() {
  const { sessionId } = Route.useParams();
  const { from } = Route.useSearch();
  const { lastEndedSession, clearLastEndedSession } = useActivePowerDialer();
  const navigate = useNavigate();
  const { t } = useTranslation('desktop_ui');

  const dismissLabel = t(
    from === 'analytics'
      ? 'components.sidebar.menu.analytics'
      : 'components.sidebar.menu.powerDialer',
  );

  // Prefer the in-memory snapshot from the just-ended flow — it has the full
  // contacts list and per-contact dialing state that the API doesn't expose.
  const hasLocalSnapshot = lastEndedSession?.session_id === sessionId;

  const { data, isLoading, error } = $apiAlloRemote.useQuery(
    'get',
    '/v1/dialing-queues/sessions/{id}',
    { params: { path: { id: sessionId } } },
    { enabled: !hasLocalSnapshot },
  );

  const fetchedSession = useMemo<EndedSessionContext | null>(() => {
    if (hasLocalSnapshot) {
      return null;
    }
    const session = data?.data as WireSessionResponse | undefined;
    if (!session?.id) {
      return null;
    }
    const { contacts, dialingState } = projectAssignedNumbers(session.assigned_numbers);
    return {
      session_id: session.id,
      queue_id: null,
      started_at: session.session_start ?? new Date().toISOString(),
      ended_at: session.session_end ?? new Date().toISOString(),
      user_name: session.owner?.name,
      line_name: session.line?.name ?? '',
      line_number: session.line?.number ?? '',
      contacts,
      dialing_state: dialingState,
    };
  }, [data, hasLocalSnapshot]);

  const session = hasLocalSnapshot ? lastEndedSession : fetchedSession;

  const handleDismiss = () => {
    if (hasLocalSnapshot) {
      clearLastEndedSession();
    }
    if (from === 'analytics') {
      void navigate({ to: '/analytics', search: { tab: 'power_dialer' } });
    } else {
      void navigate({ to: '/power-dialer' });
    }
  };

  if (session) {
    return (
      <SessionSummaryView session={session} onDismiss={handleDismiss} dismissLabel={dismissLabel} />
    );
  }

  if (isLoading) {
    return <SessionSummaryFallback />;
  }

  // Fetch failed or 404 — drop back to the queue.
  if (error) {
    return <Navigate to="/power-dialer" replace />;
  }

  return <SessionSummaryFallback />;
}

const SessionSummaryFallback = () => (
  <PageLayout>
    <PageContent>
      <div className="mx-auto w-full max-w-[640px] px-6 py-8">
        <Skeleton className="mb-2 h-3 w-24" />
        <Skeleton className="mb-6 h-7 w-48" />
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder
              key={i}
              className="h-20 rounded-xl"
            />
          ))}
        </div>
      </div>
    </PageContent>
  </PageLayout>
);
